import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { schemaApi, schemasApi, servicesApi } from "@/api/client";
import type { SchemaVariant } from "@/types/api";
import { nextSourceVariants, type SourceVariantsState } from "./schemaVariants";
import {
  canPreviewSample,
  canPreviewScript,
  type EditorKind,
  inputFillOnSelect,
  isSchemaEditorId,
  isUntitledScriptId,
  parseResourceId,
  parseSchemaRowId,
  SOURCE_EDITOR_ID,
  type StagedResource,
  type StagedSchema,
  schemaIdOfInstance,
  stableKeyOrder,
  TRANSFORM_EDITOR_ID,
} from "./stagingModel";

// The Studio editor panels' documents and content loaders: what each panel
// shows, the dirty baselines, and the race-guarded fetches that fill them.

// One cache entry per previewable resource, shared by the explorer's hover
// preview card (SchemaPreview's usePreviewContent) and the editor's sample
// loads: the engine fills samples with a fresh RNG per request, so without
// the shared entry a drop would show different fake data than the preview
// card just did.
export function resourcePreviewQueryKey(
  kind: "sample" | "script" | "schema" | null,
  id: string | undefined,
) {
  return ["resource-preview", kind, id] as const;
}

// Introspects a resource into its document-shape variants (engine-sorted,
// majority first) — null when the resource can't be previewed or has no
// shape. The variant bar and the auto-seeded schema draft ride the same
// result, cached under schemaVariantsQueryKey.
export async function fetchSchemaVariants(
  resource: StagedResource,
): Promise<SchemaVariant[] | null> {
  if (!canPreviewSample(resource.provider)) return null;
  const target = parseResourceId(resource.id);
  if (!target) return null;
  const variants = await schemaApi.getSchema(
    target.serviceName,
    target.resourceName,
  );
  return variants && variants.length > 0 ? variants : null;
}

// A synthetic document matching one variant's schema — the engine fills
// with a fresh RNG per request, hence the query-cache sharing below.
async function fillSchemaSample(
  schema: Record<string, unknown>,
): Promise<string> {
  const filled = await schemaApi.fill(schema);
  return JSON.stringify(stableKeyOrder(filled), null, 2);
}

// Introspects a resource (schema → filled sample) — /studio's resource-select
// fill. Resolves null when the resource can't be previewed.
// Exported for the explorer's hover preview card.
export async function fetchSampleDocument(
  resource: StagedResource,
): Promise<string | null> {
  const variants = await fetchSchemaVariants(resource);
  if (!variants) return null;
  return fillSchemaSample(variants[0].schema);
}

/** Cache entry for a resource's introspected variants — shared by the
 *  editor's sample loads, the variant bar and the schema-draft seeding. */
export function schemaVariantsQueryKey(id: string | undefined) {
  return ["resource-preview", "variants", id] as const;
}

/** The introspected variants through the shared cache entry. */
export function ensureSchemaVariants(
  queryClient: QueryClient,
  resource: StagedResource,
): Promise<SchemaVariant[] | null> {
  return queryClient.ensureQueryData({
    queryKey: schemaVariantsQueryKey(resource.id),
    queryFn: () => fetchSchemaVariants(resource),
  });
}

// Variant 0 keeps the bare "sample" key the hover preview card shares; the
// other variants get their own per-index entries.
function variantSampleQueryKey(id: string, index: number) {
  return index === 0
    ? resourcePreviewQueryKey("sample", id)
    : (["resource-preview", "sample", id, index] as const);
}

// One variant's filled sample through the cache — introspection itself
// comes from the shared variants entry, so switching variants never
// re-samples the collection.
function ensureVariantSample(
  queryClient: QueryClient,
  resource: StagedResource,
  index: number,
): Promise<string | null> {
  return queryClient.ensureQueryData({
    queryKey: variantSampleQueryKey(resource.id, index),
    queryFn: async () => {
      const variants = await ensureSchemaVariants(queryClient, resource);
      const variant = variants?.[index];
      return variant ? fillSchemaSample(variant.schema) : null;
    },
  });
}

// A udf script's content — /studio's loadUdfScript. Exported for the
// explorer's hover preview card.
export async function fetchScriptContent(
  resource: StagedResource,
): Promise<string | null> {
  if (!canPreviewScript(resource.provider)) return null;
  const target = parseResourceId(resource.id);
  if (!target) return null;
  const result = await servicesApi.getResourceContent(
    target.serviceName,
    target.resourceName,
  );
  return result?.content ?? null;
}

// A stored schema definition, from the service the row's id names
// (schemas live per service, TTTL-319). Exported for the explorer's hover
// preview card.
export async function fetchSchemaDefinition(
  resource: StagedResource,
): Promise<string | null> {
  const owner = parseSchemaRowId(resource.id);
  if (!owner) return null;
  const entry = await schemasApi.get(owner.serviceName, owner.entryId);
  return typeof entry?.definition === "string" ? entry.definition : null;
}

// The raw editor documents plus their dirty baselines: the definition (or
// script) a selected staged row diverged from — its row shows the dirty
// asterisk while the editor content differs; null while the editor holds
// an unsaved pseudo-row draft.
function useEditorDocuments() {
  const [inputDocument, setInputDocument] = useState("");
  const [scriptDocument, setScriptDocument] = useState("");
  const [schemaDocument, setSchemaDocument] = useState("");
  const [schemaOrigin, setSchemaOrigin] = useState<string | null>(null);
  const [scriptOrigin, setScriptOrigin] = useState<string | null>(null);
  // The introspected sample the input document was loaded from — the
  // "input edited" baseline (stagingModel.inputEdited). Unlike the script/
  // schema origins it never tracks saves: the edited input rides with the
  // pipeline, it is never written back to the source.
  const [inputOrigin, setInputOrigin] = useState<string | null>(null);
  // The input stored in the open pipeline (TTTL-191): what the open
  // loaded, updated on save/deploy — "" for scratch. The badge baseline
  // (inputEditedSinceOpen) and what a source reselect restores instead of
  // regenerating a sample (inputFillOnSelect). Lives here rather than on
  // the pipeline provider because that provider mounts BELOW staging.
  const [savedInput, setSavedInput] = useState("");

  const actions = useDocumentActions({
    schemaDocument,
    scriptDocument,
    setInputDocument,
    setInputOrigin,
    setScriptDocument,
    setScriptOrigin,
    setSchemaDocument,
    setSchemaOrigin,
  });

  return useMemo(
    () => ({
      inputDocument,
      setInputDocument,
      scriptDocument,
      setScriptDocument,
      schemaDocument,
      setSchemaDocument,
      schemaOrigin,
      setSchemaOrigin,
      scriptOrigin,
      setScriptOrigin,
      inputOrigin,
      setInputOrigin,
      savedInput,
      setSavedInput,
      ...actions,
    }),
    [
      inputDocument,
      scriptDocument,
      schemaDocument,
      schemaOrigin,
      scriptOrigin,
      inputOrigin,
      savedInput,
      actions,
    ],
  );
}

// The save/restore/clear moves on the documents — the callback half of
// the documents hook, split from the state half so both stay readable.
function useDocumentActions(state: {
  schemaDocument: string;
  scriptDocument: string;
  setInputDocument: Dispatch<SetStateAction<string>>;
  setInputOrigin: Dispatch<SetStateAction<string | null>>;
  setScriptDocument: Dispatch<SetStateAction<string>>;
  setScriptOrigin: Dispatch<SetStateAction<string | null>>;
  setSchemaDocument: Dispatch<SetStateAction<string>>;
  setSchemaOrigin: Dispatch<SetStateAction<string | null>>;
}) {
  const { schemaDocument, scriptDocument } = state;
  const { setScriptDocument, setScriptOrigin } = state;
  const { setSchemaDocument, setSchemaOrigin } = state;

  const markSchemaSaved = useCallback(
    () => setSchemaOrigin(schemaDocument),
    [setSchemaOrigin, schemaDocument],
  );

  const markScriptSaved = useCallback(
    () => setScriptOrigin(scriptDocument),
    [setScriptOrigin, scriptDocument],
  );

  // Puts a remembered instance draft back on screen — the in-flight text
  // plus the stored definition it diverged from (drives the dirty
  // asterisk) — without refetching anything.
  const restoreSchemaEdit = useCallback(
    (text: string, origin: string) => {
      setSchemaDocument(text);
      setSchemaOrigin(origin);
    },
    [setSchemaDocument, setSchemaOrigin],
  );

  const restoreScriptEdit = useCallback(
    (text: string, origin: string) => {
      setScriptDocument(text);
      setScriptOrigin(origin);
    },
    [setScriptDocument, setScriptOrigin],
  );

  const clearEditor = useClearEditor(state);

  return useMemo(
    () => ({
      markSchemaSaved,
      markScriptSaved,
      restoreSchemaEdit,
      restoreScriptEdit,
      clearEditor,
    }),
    [
      markSchemaSaved,
      markScriptSaved,
      restoreSchemaEdit,
      restoreScriptEdit,
      clearEditor,
    ],
  );
}

// Hands the given editor its zero state — split from useDocumentActions
// so neither hook outgrows a readable length.
function useClearEditor(state: {
  setInputDocument: Dispatch<SetStateAction<string>>;
  setInputOrigin: Dispatch<SetStateAction<string | null>>;
  setScriptDocument: Dispatch<SetStateAction<string>>;
  setScriptOrigin: Dispatch<SetStateAction<string | null>>;
  setSchemaDocument: Dispatch<SetStateAction<string>>;
  setSchemaOrigin: Dispatch<SetStateAction<string | null>>;
}) {
  const { setInputDocument, setInputOrigin } = state;
  const { setScriptDocument, setScriptOrigin } = state;
  const { setSchemaDocument, setSchemaOrigin } = state;
  return useCallback(
    (kind: EditorKind) => {
      const clears: Record<EditorKind, () => void> = {
        input: () => {
          setInputDocument("");
          setInputOrigin(null);
        },
        script: () => {
          setScriptDocument("");
          setScriptOrigin(null);
        },
        schema: () => {
          setSchemaDocument("");
          setSchemaOrigin(null);
        },
      };
      clears[kind]();
    },
    [
      setInputDocument,
      setInputOrigin,
      setScriptDocument,
      setScriptOrigin,
      setSchemaDocument,
      setSchemaOrigin,
    ],
  );
}

// The race guard every loader runs through — one counter for all of them,
// so only the latest request may fill an editor whichever kind it targets:
// out-of-order responses (several quick clicks) never overwrite a newer
// preview.
// `loading` is true while the latest request is in flight — anything that
// writes an editor document behind a selection (the AI Helper's Insert)
// waits on it, or the fetch would land after the write and replace it.
function useRunLatest() {
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const runLatest = useCallback(
    (
      fetchContent: () => Promise<string | null>,
      commit: (text: string) => void,
      onLoaded?: () => void,
    ) => {
      const requestId = ++requestRef.current;
      setLoading(true);
      const settle = () => {
        if (requestRef.current === requestId) setLoading(false);
      };
      fetchContent()
        .then((text) => {
          if (requestRef.current !== requestId) return;
          settle();
          if (text === null) return;
          commit(text);
          onLoaded?.();
        })
        .catch(() => {
          // Fetch failed — the editor keeps whatever it had.
          settle();
        });
    },
    [],
  );
  return { runLatest, loading };
}

type RunLatest = ReturnType<typeof useRunLatest>["runLatest"];
type ContentLoader = (resource: StagedResource, onLoaded?: () => void) => void;

// The shape the script and schema editors share: the fetched content becomes
// both the document and the dirty baseline it is compared against.
function useDocumentLoader(
  runLatest: RunLatest,
  fetchContent: (resource: StagedResource) => Promise<string | null>,
  setDocument: (text: string) => void,
  setOrigin: (text: string) => void,
): ContentLoader {
  return useCallback(
    (resource, onLoaded) =>
      runLatest(
        () => fetchContent(resource),
        (text) => {
          setDocument(text);
          setOrigin(text);
        },
        onLoaded,
      ),
    [runLatest, fetchContent, setDocument, setOrigin],
  );
}

// An edited input rides with the pipeline (Figma 14778-123822), so no
// sample fetch may clobber it, and a saved/drafted pipeline never
// regenerates its input on a source reselect — an emptied editor reverts
// to the pipeline's stored input, only scratch reloads the sample
// (stagingModel.inputFillOnSelect). The fetch goes through the hover
// card's cache entry: a drop after a preview shows the SAME generated
// document the card did (and a drop first seeds the card), instead of
// each fetch rolling new fake data.
function useSampleLoader(
  runLatest: RunLatest,
  docs: ReturnType<typeof useEditorDocuments>,
  onSourceSelected: (resource: StagedResource) => void,
): ContentLoader {
  const { inputDocument, inputOrigin, savedInput } = docs;
  const { setInputDocument, setInputOrigin } = docs;
  const queryClient = useQueryClient();
  return useCallback(
    (resource, onLoaded) => {
      // The variant bar tracks the source either way — a kept input
      // blocks the sample fill below, not the bar.
      onSourceSelected(resource);
      const action = inputFillOnSelect(inputDocument, inputOrigin, savedInput);
      if (action !== "load-sample") {
        if (action === "restore-saved") {
          setInputDocument(savedInput);
          setInputOrigin(null);
        }
        // The editor is settled, so callers waiting to switch the
        // selection (see useStageResource) go ahead.
        onLoaded?.();
        return;
      }
      runLatest(
        () => ensureVariantSample(queryClient, resource, 0),
        (text) => {
          setInputDocument(text);
          setInputOrigin(text);
        },
        onLoaded,
      );
    },
    [
      runLatest,
      queryClient,
      setInputDocument,
      setInputOrigin,
      inputDocument,
      inputOrigin,
      savedInput,
      onSourceSelected,
    ],
  );
}

// The staged source's introspected shape variants — the state behind the
// input editor's variant bar (TTTL-179 gap 4). loadSourceVariants records
// them on every source select/drop; applyVariantSample swaps the sample
// document to another shape through the editors' shared race guard.
function useSourceVariants(
  runLatest: RunLatest,
  docs: ReturnType<typeof useEditorDocuments>,
) {
  const { setInputDocument, setInputOrigin } = docs;
  const queryClient = useQueryClient();
  const [sourceVariants, setSourceVariants] =
    useState<SourceVariantsState | null>(null);
  // The resource the bar's variants belong to — kept whole (not just the
  // id) so a switch can refill previews of unstaged tree resources too.
  const resourceRef = useRef<StagedResource | null>(null);
  const requestRef = useRef(0);

  const loadSourceVariants = useCallback(
    (resource: StagedResource) => {
      resourceRef.current = resource;
      const requestId = ++requestRef.current;
      ensureSchemaVariants(queryClient, resource)
        .then((variants) => {
          if (requestRef.current !== requestId) return;
          setSourceVariants((prev) =>
            nextSourceVariants(prev, resource.id, variants),
          );
        })
        .catch(() => {
          // Introspection failed — the bar keeps whatever it showed.
        });
    },
    [queryClient],
  );

  const switchVariantSample = useCallback(
    (index: number) => {
      const resource = resourceRef.current;
      if (!resource) return;
      runLatest(
        () => ensureVariantSample(queryClient, resource, index),
        (text) => {
          setInputDocument(text);
          setInputOrigin(text);
          setSourceVariants((prev) =>
            prev && prev.resourceId === resource.id
              ? { ...prev, activeIndex: index }
              : prev,
          );
        },
      );
    },
    [runLatest, queryClient, setInputDocument, setInputOrigin],
  );

  return { sourceVariants, loadSourceVariants, switchVariantSample };
}

// The per-kind content loaders on top of the documents.
function useContentLoaders(
  runLatest: RunLatest,
  docs: ReturnType<typeof useEditorDocuments>,
  onSourceSelected: (resource: StagedResource) => void,
) {
  const loadSample = useSampleLoader(runLatest, docs, onSourceSelected);
  const loadScript = useDocumentLoader(
    runLatest,
    fetchScriptContent,
    docs.setScriptDocument,
    docs.setScriptOrigin,
  );
  const loadSchema = useDocumentLoader(
    runLatest,
    fetchSchemaDefinition,
    docs.setSchemaDocument,
    docs.setSchemaOrigin,
  );
  return { loadSample, loadScript, loadSchema };
}

// Editor documents plus their content loaders, memoized so the provider's
// context value only regenerates on real changes.
export function useEditorContents() {
  const docs = useEditorDocuments();
  const { runLatest, loading: contentLoading } = useRunLatest();
  const { sourceVariants, loadSourceVariants, switchVariantSample } =
    useSourceVariants(runLatest, docs);
  const { loadSample, loadScript, loadSchema } = useContentLoaders(
    runLatest,
    docs,
    loadSourceVariants,
  );
  return useMemo(
    () => ({
      ...docs,
      loadSample,
      loadScript,
      loadSchema,
      sourceVariants,
      switchVariantSample,
      contentLoading,
    }),
    [
      docs,
      loadSample,
      loadScript,
      loadSchema,
      sourceVariants,
      switchVariantSample,
      contentLoading,
    ],
  );
}

export type EditorContents = ReturnType<typeof useEditorContents>;

// The staged row whose content a fresh click should put in its editor —
// sample for a source, script for a transform, stored definition for a
// schema — like /studio's resource-select. Schema rows select as
// per-block instances; the instance id resolves to its schema.
export function stagedContentFor(
  id: string,
  lists: {
    sources: StagedResource[];
    transforms: StagedResource[];
    schemas: StagedSchema[];
  },
): { kind: "sample" | "script" | "schema"; resource: StagedResource } | null {
  const source = lists.sources.find((r) => r.id === id);
  if (source) return { kind: "sample", resource: source };
  const transform = lists.transforms.find((r) => r.id === id);
  if (transform) return { kind: "script", resource: transform };
  const schemaId = schemaIdOfInstance(id) ?? id;
  const schema = lists.schemas.find((s) => s.id === schemaId);
  if (schema) return { kind: "schema", resource: schema };
  return null;
}

// Something is always selected, defaulting to SOURCE: with nothing else
// picked (and no tree pill), the staged source row is selected — or the
// SOURCE editor row while the section is empty (Figma default frame).
export function defaultSelection(
  selectedId: string | null,
  hasTreePill: boolean,
  sources: StagedResource[],
): string | null {
  if (selectedId !== null) return selectedId;
  if (hasTreePill) return null;
  if (sources.length === 0) return SOURCE_EDITOR_ID;
  return sources[0]?.id ?? null;
}

// Which editor the Studio panel shows: the schema editor for the sink-tab
// SCHEMA pseudo-rows, selected staged schemas and tree schema previews; the
// transform script editor for the TRANSFORM pseudo-row, selected staged
// transforms and tree script previews; the input editor otherwise.
export function resolveEditorKind(
  selected: string | null,
  treeKind: EditorKind | null,
  transforms: StagedResource[],
  schemas: StagedSchema[],
): EditorKind {
  if (isSchemaEditorId(selected)) return "schema";
  // Plain ids come from the classic bar; the stage tabs select schema
  // rows as per-block instances.
  const selectedSchemaId = schemaIdOfInstance(selected) ?? selected;
  if (schemas.some((s) => s.id === selectedSchemaId)) return "schema";
  if (selected === null) return treeKind ?? "input";
  if (selected === TRANSFORM_EDITOR_ID || isUntitledScriptId(selected))
    return "script";
  if (transforms.some((r) => r.id === selected)) return "script";
  return "input";
}

/** Explorer leaf whose content the editor is showing (active pill in the
 *  tree) and which editor it belongs in — like /studio's explorerActiveIds. */
export type TreePill = { id: string; kind: EditorKind } | null;
export type SetTreePill = Dispatch<SetStateAction<TreePill>>;
export type SetSelectedId = Dispatch<SetStateAction<string | null>>;

// Tree clicks: load content AND move the single selection to the tree
// (active pill, nav bar deselected). A selected staged schema row deselects
// on a tree preview — it stays staged (Figma 14442-316243).
export function useTreePreviews(
  editor: EditorContents,
  setEditorSource: SetTreePill,
  setSelectedId: SetSelectedId,
) {
  const { loadSample, loadScript, loadSchema } = editor;
  const showFromTree = useCallback(
    (
      load: (resource: StagedResource, onLoaded?: () => void) => void,
      kind: EditorKind,
    ) =>
      (resource: StagedResource) =>
        load(resource, () => {
          setEditorSource({ id: resource.id, kind });
          setSelectedId(null);
        }),
    [setEditorSource, setSelectedId],
  );
  const showSourceSample = useMemo(
    () => showFromTree(loadSample, "input"),
    [showFromTree, loadSample],
  );
  const showScript = useMemo(
    () => showFromTree(loadScript, "script"),
    [showFromTree, loadScript],
  );
  const showSchema = useMemo(
    () => showFromTree(loadSchema, "schema"),
    [showFromTree, loadSchema],
  );
  return useMemo(
    () => ({ showSourceSample, showScript, showSchema }),
    [showSourceSample, showScript, showSchema],
  );
}
